import React from 'react';
import { CitationResultsSnippets } from './citator-search/CaseResultSnippets';
import { LevelOfDiscussion } from './citator-search/LevelOfDiscussion';
import { StyledResultTitle } from 'pages/search/SearchResult';
import { CaseMeta } from 'components/cases/case/CaseMeta';
import { StyledLinkedResultSnippet } from 'components/common/search/LinkedResultSnippets';
import { CasePillButton } from 'pages/cases/case-pill-button';
import { useAssetAccess } from 'components/asset-access';
import styled from 'styled-components';
import { CaseSearchEdge } from '__generated__/graphql';
import { CaseLink } from 'features/link-helpers/CaseLink';
import { useSearchParams } from 'react-router-dom';

export interface CaseSearchResultProps {
  result: CaseSearchEdge;
  className?: string;
}

export const CaseSearchResult = ({
  result,
  className,
}: CaseSearchResultProps) => {
  const {
    snippets,
    citingTreatment,
    node: {
      name,
      cite,
      date,
      courtName,
      treatmentAttitudeName,
      treatmentDescription,
      citationCount,
      lastCitedOn,
      exampleTreatingCase,
      judicataId,
    },
  } = result;

  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');

  const hasCitingTreatment = !query && citingTreatment;
  const { hasCitatorAccess } = useAssetAccess();

  return (
    <StyledCaseSearchResultItem
      className={className || ''}
      $hasCitingTreatment={!!hasCitingTreatment}
    >
      {hasCitingTreatment ? (
        <LevelOfDiscussion
          description={citingTreatment.treatmentDescription}
          level={citingTreatment.levelOfDiscussion}
          attitude={citingTreatment.attitudeName}
        />
      ) : null}
      <div className="result">
        <StyledCaseResultTitle>
          {hasCitatorAccess ? (
            <span className="case-pill-button">
              <CasePillButton
                case={{
                  judicataId,
                  treatmentAttitudeName,
                  treatmentDescription,
                  citationCount,
                  lastCitedOn,
                  exampleTreatingCase,
                }}
              />
            </span>
          ) : null}
          <CaseLink judicataId={judicataId!} query={query}>
            {name}
          </CaseLink>
        </StyledCaseResultTitle>
        <CaseMeta cite={cite} date={date} courtName={courtName} />
        {!query ? (
          <CitationResultsSnippets
            judicataId={judicataId}
            snippets={snippets}
            query={query}
          />
        ) : (
          <StyledCaseSnippet>
            {(snippets || []).map(snippet => (
              <CaseLink
                key={snippet?.position}
                judicataId={judicataId}
                query={query}
                hash={snippet?.position}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: snippet?.text || '',
                  }}
                />
              </CaseLink>
            ))}
          </StyledCaseSnippet>
        )}
      </div>
    </StyledCaseSearchResultItem>
  );
};

interface StyledCaseSearchResultItemProps {
  $hasCitingTreatment?: boolean;
}

const StyledCaseSearchResultItem = styled.li<StyledCaseSearchResultItemProps>`
  display: flex;
  margin-bottom: 7px;
  max-width: ${({ $hasCitingTreatment }) =>
    $hasCitingTreatment ? 930 : 780}px;
  padding: 6px 0 7px;

  .case-pill-button {
    font-size: 11px;
    margin-right: 12px;
    position: relative;
    vertical-align: 1px;
    button {
      padding: 6px 2.4em 5px 8px;
      span {
        margin-left: 5px;
      }
    }
  }

  .result {
    flex: 1 1 auto;
    margin-left: 32px;
    dl {
      height: auto;
      line-height: 1.5;
      margin-bottom: 6px;
      color: var(--COLOR_COOL_GRAY);
    }
  }
`;

const StyledCaseResultTitle = styled(StyledResultTitle)`
  margin-bottom: 11px;
`;

const StyledCaseSnippet = styled(StyledLinkedResultSnippet)`
  a,
  span {
    line-height: 1.25;
    margin-bottom: 11px;
  }
`;
